import { logger } from '@meshwatch/logging';
import { SQS } from 'aws-sdk/clients/all';
import {
  CreateQueueRequest,
  ReceiveMessageRequest,
  SendMessageRequest,
  SendMessageBatchRequest,
  ClientConfiguration,
} from 'aws-sdk/clients/sqs';

import { sqsClientConfiguration } from './configuration';

type SendQueueMessageRequest = Omit<SendMessageRequest, 'QueueUrl'>;
type SendQueueMessageBatchRequest = Omit<SendMessageBatchRequest, 'QueueUrl'>;
type ReceiveQueueMessageRequest = Omit<ReceiveMessageRequest, 'QueueUrl'>;
type WithQueueUrl<T> = T & { QueueUrl: string };

class SQSClient {
  public readonly sqs: SQS;

  public constructor(sqs: SQS) {
    this.sqs = sqs;
  }

  public getQueueURL = (name: string) => {
    return this.sqs.getQueueUrl({ QueueName: name }).promise();
  };

  public sendMessage = (params: SendMessageRequest) => {
    logger.debug('[SQS]: sending message', { params });
    return this.sqs
      .sendMessage(params)
      .promise()
      .catch(err => {
        logger.error('[SQS]: failed to send message', err, { params });
        throw err;
      });
  };

  public sendMessageBatch = (params: SendMessageBatchRequest) => {
    logger.debug('[SQS]: sending message batch', { params });
    return this.sqs
      .sendMessageBatch(params)
      .promise()
      .catch(err => {
        logger.error('[SQS]: failed to send message batch', err, { params });
        throw err;
      });
  };

  public receiveMessage = (params: ReceiveMessageRequest) => {
    logger.debug('[SQS]: receiving message', { params });
    return this.sqs
      .receiveMessage(params)
      .promise()
      .catch(err => {
        logger.error('[SQS]: failed to receive message', err, { params });
        throw err;
      });
  };

  public createQueue = (params: CreateQueueRequest) => {
    logger.info('[SQS]: creating queue', { params });
    return this.sqs
      .createQueue(params)
      .promise()
      .catch(err => {
        logger.error('[SQS]: failed to create queue', err, { params });
        throw err;
      });
  };
}

const defaultSqsConfiguration: ClientConfiguration = sqsClientConfiguration();

logger.debug('[SQS]: Default client configuration', { defaultSqsConfiguration });

export const DEFAULT_SQS_CLIENT = new SQS(defaultSqsConfiguration);

export class SQSQueue {
  private readonly sqsClient: SQSClient;
  private readonly QueueName: string;
  private QueueUrl: string | undefined;

  public constructor(QueueName: string, sqsClient: SQS = DEFAULT_SQS_CLIENT) {
    this.QueueName = QueueName;
    this.sqsClient = new SQSClient(sqsClient);
  }

  public getQueueUrl = (): Promise<string> => {
    if (this.QueueUrl) {
      return Promise.resolve(this.QueueUrl);
    }

    return this.sqsClient.getQueueURL(this.QueueName).then(({ QueueUrl }) => {
      if (!QueueUrl) {
        throw new Error(`[SQS]: Could not getQueueUrl for ${this.QueueName}`);
      }

      logger.debug(`[SQS]: queue ${QueueUrl} warmed up`);
      this.QueueUrl = QueueUrl;
      return this.QueueUrl;
    });
  };

  private async queueParams<T>(params: T): Promise<WithQueueUrl<T>> {
    const QueueUrl = await this.getQueueUrl();
    return { ...params, QueueUrl };
  }

  public createQueue = () => {
    return this.sqsClient.createQueue({ QueueName: this.QueueName });
  };

  public sendMessage = (params: SendQueueMessageRequest) => {
    return this.queueParams(params).then(this.sqsClient.sendMessage);
  };

  public sendMessageBatch = (params: SendQueueMessageBatchRequest) => {
    return this.queueParams(params).then(this.sqsClient.sendMessageBatch);
  };

  public receiveMessage = (params: ReceiveQueueMessageRequest) => {
    return this.queueParams(params).then(this.sqsClient.receiveMessage);
  };
}
